import { Divider, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCopyToClipboard } from 'react-use'

import { API } from '@/api'
import { ServerImageInfo, ServerSnapInfo } from '@/api/type-server.ts'
import EditIcon from '@/assets/auto-deploy/edit.svg'
import InformationIcon from '@/assets/auto-deploy/information.svg'
import CopyIcon from '@/assets/icons/copy.svg?react'
import { Loading, message, Trans } from '@/components'
import { MachineAttrTag } from '@/consts/image'
import { useUnifiedContext } from '@/contexts/UnifiedContext'
import { Deployment } from '@/models/deployment.ts'
import EditScalingPolicyModal from '@/pages/auto-deploy/components/EditScalingPolicyModal'
import EditTtlModal from '@/pages/auto-deploy/components/EditTtlModal'
import { formatRamMB } from '@/utils/formatter.ts'
import { fetchAllPages } from '@/utils/pagination'

interface props {
  _deployment: Deployment
  _enabled?: boolean
  onUpdate?: () => void
}

export default function DeploymentTarget({ _deployment, onUpdate }: props) {
  const { t } = useTranslation('autoDeploy')
  const { mode, teamResID } = useUnifiedContext()
  const [loading, setLoading] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [imageSpec, setImageSpec] = useState<ServerImageInfo>()
  const [snapSpec, setSnapSpec] = useState<ServerSnapInfo>()
  const [state, copyToClipboard] = useCopyToClipboard()
  const [domains, setDomains] = useState<number[]>([])

  // Modal states
  const [showTtlModal, setShowTtlModal] = useState(false)
  const [showScalingPolicyModal, setShowScalingPolicyModal] = useState(false)

  // Fetch domains on mount
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const res = await API.getDomainList()
        if (res.code === 0) {
          setDomains(res.domainInfos?.map((d) => d.domain) || [])
        }
      } catch (error) {
        console.error('Failed to fetch domains:', error)
      }
    }
    fetchDomains()
  }, [])

  const getImages = useCallback(
    async (imageId: number) => {
      setLoading(true)

      try {
        const filters = {
          machine_category: _deployment.runtimeEnv.hardwareEnv.resource.machineCategory,
        }
        const images = await fetchAllPages({
          fetchPage: (page, pageSize) => API.getImageList({ ...filters, page, per_page: pageSize }),
          getItems: (response) => response.images,
        })
        let image = images.find((item) => item.id === imageId)

        // The unfiltered endpoint currently returns container images by default.
        // VM images must be requested with their machine attribute tag.
        if (!image) {
          const vmImages = await fetchAllPages({
            fetchPage: (page, pageSize) =>
              API.getImageList({
                ...filters,
                page,
                per_page: pageSize,
                machine_attr_tag: MachineAttrTag.VirtualMachine,
              }),
            getItems: (response) => response.images,
          })
          image = vmImages.find((item) => item.id === imageId)
        }

        setImageSpec(image)
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
        setHasFetched(true)
      }
    },
    [_deployment.resID, _deployment.runtimeEnv.hardwareEnv.resource.machineCategory],
  )

  const getSnapshots = useCallback(
    (snapshotId: number) => {
      setLoading(true)

      if (mode === 'platform') {
        // Platform mode: only query personal snapshots
        fetchAllPages({
          fetchPage: (page, pageSize) =>
            API.getSnapList({ page, per_page: pageSize, domains: [-1] }),
          getItems: (response) => response.snapshots,
        })
          .then((snapshots) => {
            setSnapSpec(snapshots.find((snapshot) => Number(snapshot.id) === snapshotId))
          })
          .catch((error) => {
            console.error(error)
          })
          .finally(() => {
            setLoading(false)
            setHasFetched(true)
          })
      } else {
        // Team mode: wait for domains to be loaded before querying
        if (domains.length === 0) {
          setLoading(false)
          return
        }

        // Team mode: query both personal and team shared snapshots in parallel
        Promise.all([
          fetchAllPages({
            fetchPage: (page, pageSize) =>
              API.getSnapList({ page, per_page: pageSize, domains: [-1] }),
            getItems: (response) => response.snapshots,
          }),
          fetchAllPages({
            fetchPage: (page, pageSize) =>
              API.getTeamSharedSnapshots({
                page,
                per_page: pageSize,
                res_id: teamResID!,
                regions: domains,
              }),
            getItems: (response) => response.snapshots,
          }),
        ])
          .then(([personalSnapshots, teamSnapshots]) => {
            // First check personal snapshots
            const personalSnapshot = personalSnapshots.find(
              (snapshot) => Number(snapshot.id) === snapshotId,
            )
            if (personalSnapshot) {
              setSnapSpec(personalSnapshot)
              return
            }

            // Then check team shared snapshots
            const teamSnapshot = teamSnapshots.find(
              (snapshot) => Number(snapshot.snapshot.id) === snapshotId,
            )
            if (teamSnapshot) {
              setSnapSpec(teamSnapshot.snapshot)
            }
          })
          .catch((error) => {
            console.error(error)
          })
          .finally(() => {
            setLoading(false)
            setHasFetched(true)
          })
      }
    },
    [_deployment.resID, mode, teamResID, domains],
  )

  const memoizedDeployment = useMemo(() => _deployment, [_deployment.resID])

  useEffect(() => {
    if (memoizedDeployment) {
      const { fromSnapshotId, fromImageId } = memoizedDeployment.runtimeEnv.systemEnv

      if (fromImageId !== 0) {
        getImages(memoizedDeployment.runtimeEnv.systemEnv.fromImageId)
      }

      if (fromSnapshotId !== 0) {
        getSnapshots(memoizedDeployment.runtimeEnv.systemEnv.fromSnapshotId)
      }
    }
  }, [
    getImages,
    getSnapshots,
    memoizedDeployment.runtimeEnv.systemEnv.fromImageId,
    memoizedDeployment.runtimeEnv.systemEnv.fromSnapshotId,
  ])

  // Handle case where no fetch is needed (both IDs are 0)
  useEffect(() => {
    const { fromSnapshotId, fromImageId } = _deployment.runtimeEnv.systemEnv
    if (fromImageId === 0 && fromSnapshotId === 0) {
      setHasFetched(true)
    }
  }, [_deployment.runtimeEnv.systemEnv])

  useEffect(() => {
    if (state.value) {
      message.success(t('common.copied'))
    }
  }, [state, t])

  return (
    <>
      {(loading || !hasFetched) && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!loading && hasFetched && (
        <div className="bg-neutral-50 rounded-lg flex flex-wrap t-sm text-tertiary gap-y-4">
          <div className="w-full grid grid-cols-2 gap-y-4 rounded-lg">
            <div className="col-span-1 grid grid-cols-2 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="common.id" ns="autoDeploy">
                    ID
                  </Trans>
                </div>
                <div className="t-sb text-primary">{_deployment.resID}</div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.name" ns="autoDeploy">
                    Auto Deploy Name
                  </Trans>
                </div>
                <div className="t-sb text-primary">{_deployment.name}</div>
              </div>
            </div>
            <div className="col-span-2 grid grid-cols-4 space-x-4">
              <div className="col-span-4 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.description" ns="autoDeploy">
                    Auto Deploy Description
                  </Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {_deployment.description}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="deployTarget.instancePreview" ns="autoDeploy">
                Instance Preview
              </Trans>
            </div>
            <Divider className="my-0 pr-4" />
          </div>

          <div className="w-full grid grid-cols-4 gap-y-4 rounded-lg">
            {_deployment.runtimeEnv.systemEnv.fromImageId !== 0 && (
              <div className="col-span-4 grid grid-cols-4 space-x-4">
                <div className="col-span-1 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deployTarget.image" ns="autoDeploy">
                      Image
                    </Trans>
                  </div>
                  <div className="t-sb text-primary">
                    {imageSpec?.alias || _deployment.runtimeEnv.systemEnv.fromImageId}
                  </div>
                </div>
                <div className="col-span-3 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deployTarget.imageDescription" ns="autoDeploy">
                      Image description
                    </Trans>
                  </div>
                  <div className="t-sb text-primary">{imageSpec?.description || '-'}</div>
                </div>
              </div>
            )}
            {_deployment.runtimeEnv.systemEnv.fromSnapshotId !== 0 && (
              <div className="col-span-4 grid grid-cols-4 space-x-4">
                <div className="col-span-1 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deployTarget.snapshot" ns="autoDeploy">
                      Snapshot
                    </Trans>
                  </div>
                  <div className="t-sb text-primary">
                    {snapSpec?.flag?.subject || _deployment.runtimeEnv.systemEnv.fromSnapshotId}
                  </div>
                </div>
                <div className="col-span-3 space-y-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="deployTarget.snapshotCreatedTime" ns="autoDeploy">
                      Snapshot created time
                    </Trans>
                  </div>
                  <div className="t-sb text-primary">
                    {snapSpec?.flag
                      ? dayjs(snapSpec.flag.createTime).format('YYYY-MM-DD HH:mm:ss')
                      : '-'}
                  </div>
                </div>
              </div>
            )}
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.gpuModel" ns="autoDeploy">
                    GPU Model
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.gpuName || '-'}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.gpus" ns="autoDeploy">
                    GPUs
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.gpus}
                </div>
              </div>
              <div className="col-span-2 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.gpuRam" ns="autoDeploy">
                    GPU RAM
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(_deployment.runtimeEnv.hardwareEnv.resource.gpuRamMB)}
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.cpuModel" ns="autoDeploy">
                    CPU Model
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.cpuName}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.vCpus" ns="autoDeploy">
                    vCPUs
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.cpus}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.ram" ns="autoDeploy">
                    RAM
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(_deployment.runtimeEnv.hardwareEnv.resource.ramMB)}
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.storage" ns="autoDeploy">
                    Storage
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {_deployment.runtimeEnv.hardwareEnv.resource.diskGB}{' '}
                  <Trans i18nKey="common.units.gb" ns="autoDeploy">
                    GB
                  </Trans>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="deployTarget.cmd" ns="autoDeploy">
                Service & Start Command
              </Trans>
            </div>
            <Divider className="my-0 pr-2" />
          </div>

          <div className="w-full grid grid-cols-4 gap-y-4 rounded-lg">
            <div className="col-span-4 grid grid-cols-3 space-x-4">
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.accessMethodTitle" ns="autoDeploy">
                    Access Method
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  <Trans i18nKey="deployTarget.sharedMethod" ns="autoDeploy">
                    Shared Method
                  </Trans>
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">URL</div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  <div className="min-w-0 break-all">{_deployment.gateway.url}</div>
                  <div
                    className="cursor-pointer flex gap-x-1 items-center t-sm text-highlight"
                    onClick={() => {
                      copyToClipboard(_deployment.gateway.url)
                    }}
                  >
                    <CopyIcon width={16} height={16} />
                    {t('common.copy')}
                  </div>
                </div>
              </div>
              <div className="col-span-1 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.port" ns="autoDeploy">
                    Port
                  </Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {_deployment.gateway.port > 0 ? (
                    <>
                      <div>{_deployment.gateway.port}</div>
                      <div
                        className="cursor-pointer flex gap-x-1 items-center t-sm text-highlight"
                        onClick={() => {
                          copyToClipboard(_deployment.gateway.port.toString())
                        }}
                      >
                        <CopyIcon width={16} height={16} />
                        {t('common.copy')}
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-4 space-y-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="deployTarget.cmd" ns="autoDeploy">
                    Start Command
                  </Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {_deployment.runtimeEnv.systemEnv.cmd.length > 0 ? (
                    <>
                      <div>{_deployment.runtimeEnv.systemEnv.cmd}</div>
                      <div
                        className="cursor-pointer flex gap-x-1 items-center t-sm text-highlight"
                        onClick={() => {
                          copyToClipboard(_deployment.runtimeEnv.systemEnv.cmd)
                        }}
                      >
                        <CopyIcon width={16} height={16} />
                        {t('common.copy')}
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="deployTarget.deploymentControl" ns="autoDeploy">
                Deployment Control
              </Trans>
            </div>
            <Divider className="my-0 pr-2" />
          </div>

          <div
            className="w-full grid grid-cols-4 lg:grid-cols-2 sm:grid-cols-1 gap-4"
            data-ai-component="deployment-policy-summary"
          >
            <div className="space-y-1 min-w-0">
              <div className="t-xs text-secondary">{t('scalingPolicy.mode')}</div>
              <div className="flex items-center gap-1 flex-wrap">
                <span className="t-sb text-primary">
                  {t(_deployment.scalingMode === 1 ? 'scalingPolicy.auto' : 'scalingPolicy.manual')}
                </span>
                <button
                  type="button"
                  className="t-sm text-highlight inline-flex items-center gap-1"
                  aria-label={t('scalingPolicy.editTitle')}
                  onClick={() => setShowScalingPolicyModal(true)}
                >
                  <img src={EditIcon} alt="" />
                  {t('action.edit')}
                </button>
              </div>
            </div>
            <div className="space-y-1 min-w-0">
              <div className="t-xs text-secondary">{t('deployTarget.maxNumberIns')}</div>
              <div className="t-sb text-primary">{_deployment.maxReplicas}</div>
            </div>
            {_deployment.scalingMode === 1 && (
              <>
                <div className="space-y-1 min-w-0">
                  <div className="t-xs text-secondary">{t('scalingPolicy.concurrency')}</div>
                  <div className="t-sb text-primary">
                    {_deployment.concurrencyPerReplica}{' '}
                    <span className="text-tertiary">{t('scalingPolicy.requests')}</span>
                  </div>
                </div>
                <div className="space-y-1 min-w-0">
                  <div className="t-xs text-secondary flex items-center gap-1">
                    {t('scalingPolicy.pending')}
                    <Tooltip title={t('scalingPolicy.pendingHint')}>
                      <button
                        type="button"
                        aria-label={t('scalingPolicy.pendingHint')}
                        className="shrink-0"
                      >
                        <img src={InformationIcon} alt="" />
                      </button>
                    </Tooltip>
                  </div>
                  <div className="t-sb text-primary">
                    {_deployment.maxPendingRequests}{' '}
                    <span className="text-tertiary">{t('scalingPolicy.requests')}</span>
                  </div>
                </div>
              </>
            )}
            <div
              className={_deployment.scalingMode === 1 ? 'col-span-full space-y-1' : 'space-y-1'}
            >
              <div className="t-xs text-secondary">{t('deployTarget.ttlSeconds')}</div>
              <div className="flex items-center gap-1 flex-wrap">
                <span className="t-sb text-primary">
                  {(_deployment.ttlSeconds || 0) / 60}{' '}
                  <span className="text-tertiary">{t('common.units.minutes')}</span>
                </span>
                <button
                  type="button"
                  className="t-sm text-highlight inline-flex items-center gap-1"
                  aria-label={t('deployTarget.ttlSeconds')}
                  onClick={() => setShowTtlModal(true)}
                >
                  <img src={EditIcon} alt="" />
                  {t('action.edit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit TTL Modal */}
      <EditTtlModal
        open={showTtlModal}
        onClose={() => setShowTtlModal(false)}
        deployment={_deployment}
        onUpdate={onUpdate}
      />

      {/* Edit scaling policy */}
      <EditScalingPolicyModal
        open={showScalingPolicyModal}
        onClose={() => setShowScalingPolicyModal(false)}
        deployment={_deployment}
        onUpdate={onUpdate}
      />
    </>
  )
}
